import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../context/AuthContext';
import StatusPill from '../../components/admin/StatusPill';
import { cn } from '../../lib/utils';

const money = (value, compact = true) => {
  const n = Number(value || 0);
  if (compact && Math.abs(n) >= 1000) {
    return `€${Math.round(n / 1000).toLocaleString()}K`;
  }
  return `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const relativeDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const diff = Math.floor((today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const Kpi = ({ label, value, testId }) => (
  <div className="px-4 py-3 md:px-5 md:py-4 min-w-[140px]" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
    <p className="text-lg md:text-xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</p>
  </div>
);

export default function AdminOverviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [attention, setAttention] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activity, setActivity] = useState([]);

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    const safe = (p) => p.catch(() => ({ data: {} }));
    const [s, a, p, act] = await Promise.all([
      safe(api.get('/admin/workspace/summary')),
      safe(api.get('/admin/workspace/attention?limit=6')),
      safe(api.get('/admin/workspace/projects')),
      safe(api.get('/admin/workspace/activity?limit=8')),
    ]);
    setSummary(s.data || null);
    setAttention(a.data.items || []);
    setProjects(p.data.projects || []);
    setActivity(act.data.events || []);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-9 w-9 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-overview-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Administration</p>
          <h1 className="text-xl md:text-2xl font-heading mt-1">{greeting()}</h1>
          <p className="text-sm text-foreground-muted mt-0.5">
            {attention.length > 0
              ? `${attention.length} item${attention.length === 1 ? '' : 's'} need your attention.`
              : 'Nothing needs your attention right now.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} data-testid="dashboard-refresh-btn">
          <RefreshCw className={cn('h-4 w-4 md:mr-1.5', refreshing && 'animate-spin')} />
          <span className="hidden md:inline">Refresh</span>
        </Button>
      </div>

      {/* KPI strip */}
      <div
        className="bg-white rounded-xl border border-black/[0.06] flex overflow-x-auto divide-x divide-black/[0.05]"
        data-testid="admin-kpi-strip"
      >
        <Kpi label="Portfolio NAV" value={money(summary?.portfolio_nav)} />
        <Kpi label="Total CAPEX" value={money(summary?.total_capex)} testId="kpi-total-capex" />
        <Kpi label="Capital raised" value={money(summary?.capital_raised)} />
        <Kpi label="Returns paid" value={money(summary?.returns_paid)} />
        <Kpi label="Active projects" value={summary?.active_projects ?? 0} />
        <Kpi label="Active investors" value={summary?.active_investors ?? 0} />
      </div>

      {/* Needs attention */}
      <section className="bg-white rounded-xl border border-black/[0.06]" data-testid="needs-attention">
        <div className="flex items-center justify-between px-4 md:px-5 py-3.5 border-b border-black/[0.05]">
          <h2 className="text-sm font-medium">Needs attention</h2>
          <button
            onClick={() => navigate('/admin/tasks')}
            className="text-xs font-medium text-primary hover:opacity-70 inline-flex items-center gap-1"
            data-testid="view-all-tasks"
          >
            All tasks <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        {attention.length === 0 ? (
          <div className="px-4 md:px-5 py-10 text-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium">You're all caught up</p>
            <p className="text-xs text-foreground-muted mt-1">
              Subscriptions, KYC reviews and project setup gaps will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-black/[0.05]">
            {attention.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-slate-50/70 transition-colors"
                data-testid={`attention-item-${item.id}`}
              >
                <AlertCircle
                  className={cn('h-4 w-4 flex-shrink-0', item.priority === 'high' ? 'text-red-500' : 'text-amber-500')}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{item.entity || item.project || '—'}</p>
                  <p className="text-xs text-foreground-muted truncate">
                    {item.reason}
                    {item.project && item.entity !== item.project ? ` · ${item.project}` : ''}
                  </p>
                </div>
                {item.value ? (
                  <span className="hidden sm:block text-sm font-medium tabular-nums">{money(item.value, false)}</span>
                ) : null}
                <StatusPill status={item.status} className="hidden md:inline-flex" />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-primary"
                  onClick={() => navigate(item.action_route)}
                  data-testid={`attention-action-${item.id}`}
                >
                  {item.action_label}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        {/* Projects */}
        <section className="bg-white rounded-xl border border-black/[0.06] overflow-hidden" data-testid="dashboard-projects">
          <div className="flex items-center justify-between px-4 md:px-5 py-3.5 border-b border-black/[0.05]">
            <h2 className="text-sm font-medium">Projects</h2>
            <button
              onClick={() => navigate('/admin/v2/projects')}
              className="text-xs font-medium text-primary hover:opacity-70 inline-flex items-center gap-1"
            >
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>

          {projects.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium">No projects yet</p>
              <p className="text-xs text-foreground-muted mt-1 mb-4">Create your first investment project.</p>
              <Button size="sm" onClick={() => navigate('/admin/v2/projects?create=1')}>New project</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-foreground-muted text-xs">
                    <th className="text-left font-medium px-4 md:px-5 py-2.5">Project</th>
                    <th className="text-left font-medium px-3 py-2.5">Status</th>
                    <th className="text-right font-medium px-3 py-2.5">Raised</th>
                    <th className="text-right font-medium px-3 py-2.5">Funded</th>
                    <th className="text-right font-medium px-3 py-2.5 hidden md:table-cell">Investors</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/admin/v2/projects/${p.id}`)}
                      className="border-t border-black/[0.05] hover:bg-slate-50/70 cursor-pointer"
                      data-testid={`dashboard-project-${p.id}`}
                    >
                      <td className="px-4 md:px-5 py-3">
                        <p className="font-medium truncate max-w-[220px]">{p.title}</p>
                        <p className="text-xs text-foreground-muted font-mono">{p.code}</p>
                      </td>
                      <td className="px-3 py-3"><StatusPill status={p.status} /></td>
                      <td className="px-3 py-3 text-right tabular-nums">{p.funding_raised ? money(p.funding_raised) : '—'}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{p.funding_target ? `${p.funding_progress}%` : '—'}</td>
                      <td className="px-3 py-3 text-right tabular-nums hidden md:table-cell">{p.investors_count}</td>
                      <td className="px-4 py-3 text-right">
                        <ArrowRight className="h-3.5 w-3.5 text-foreground-muted inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section className="bg-white rounded-xl border border-black/[0.06]" data-testid="dashboard-activity">
          <div className="px-4 md:px-5 py-3.5 border-b border-black/[0.05]">
            <h2 className="text-sm font-medium">Recent activity</h2>
          </div>
          {activity.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium">No activity yet</p>
              <p className="text-xs text-foreground-muted mt-1">
                Subscriptions, NAV updates and project changes will show up here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-black/[0.05]">
              {activity.map((event, i) => (
                <div key={`${event.date}-${i}`} className="px-4 md:px-5 py-3" data-testid="activity-item">
                  <p className="text-sm truncate">{event.title}</p>
                  <p className="text-xs text-foreground-muted mt-0.5">
                    {relativeDay(event.date)}{event.subtitle ? ` · ${event.subtitle}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
